import React from 'react';
import PropTypes from 'prop-types';

class ScrollSpy extends React.Component {
  domElement;
  linkElements;
  targetElements;
  isScrolling;

  constructor(props) {
    super(props);
  }

  componentDidUpdate() {
    // The child nodes will be the link elements ('<a>') passed to this component.
    let domNodes = this.domElement.childNodes;
    domNodes = Array.prototype.slice.call(domNodes).filter(n => n.nodeName === 'A');

    if (!domNodes.length) { return; }

    // We'll keep a list of the ids, the link node, and the target element nodes as well to ease
    // on DOM manipulation; this way we do not have to constantly query the DOM.
    this.linkElements = Array.prototype.slice.call(domNodes).map((linkNode) => {
      const targetNode = document.getElementById(linkNode.getAttribute('href').replace(/^#/, ''));

      return {
        active: false,
        id: linkNode.getAttribute('href').replace(/^#/, ''),
        targetNode: targetNode,
        linkNode: linkNode
      };
    });

    this.handleScroll();
  }

  componentDidMount() {
    this.domElement.addEventListener('click', this.handleClick.bind(this));
    window.addEventListener('scroll', this.handleScroll.bind(this));

    this.handleScroll();
  }

  componentWillUnmount() {
    this.domElement.removeEventListener('click', this.handleClick.bind(this));
    window.removeEventListener('scroll', this.handleScroll.bind(this));
  }

  render() {
    return (
      <div className={this.props.className} ref={c => this.domElement = c}>
        { this.props.children }
      </div>
    );
  }

  handleClick(event) {
    // So that we do not have to attach event handlers to each of the link elements, we can just
    // listen to any click event on this component and handle those that pertain to sections' links
    if (event.target.tagName.toLowerCase() !== 'a') { return; }

    event.preventDefault();
    this.scrollToItem(event.target.getAttribute('href').replace(/^#/, ''), true);
  }

  handleScroll() {
    // We don't want the spy to keep changing the links' states while we're smooth scrolling
    // to the target element, hence why we add a check to halt the handling until 'checkIfScrollToIsFinished'
    // completes. `window.scrollTo' doesn't provide a callback, unfortunately.
    if (!this.linkElements || this.isScrolling) { return; }

    const scrollPosition = window.scrollY;
    const activeElement = this.targetElementForPosition(scrollPosition);

    this.updateLinksStates(activeElement);
  }

  updateLinksStates(activeElement) {
    let targetId = activeElement ? activeElement.id : null;

    this.linkElements.forEach((el) => {
      el.active = (el.id === targetId);

      const containsActiveClass = el.linkNode.classList.contains(this.props.activeClassname);

      if (el.active) {
        if (!containsActiveClass) {
          el.linkNode.classList.add(this.props.activeClassname);
        }
      } else {
        if (containsActiveClass) {
          el.linkNode.classList.remove(this.props.activeClassname);
        }
      }
    });
  }

  targetElementForPosition(position) {
    const scrollPosition = position + this.props.offset;

    // Here we find the first element that's visible in the DOM/viewport (the remaining will be below it)
    return this.linkElements.filter((el) => el.targetNode.offsetTop < scrollPosition + 1).slice(-1)[0];
  }

  scrollToItem(id, smooth = false) {
    const element = document.getElementById(id);

    if (!element) { return; }

    const elementPosition = window.pageYOffset + element.getBoundingClientRect().top;
    const scrollPosition = elementPosition - this.props.offset;
    const targetElement = this.targetElementForPosition(scrollPosition);

    const checkIfScrollToIsFinished = () => {
      let prevPosition;

      const scrollPositionCheckInterval = setInterval(() => {
        if (window.scrollY === prevPosition) {
          this.isScrolling = false;
          clearInterval(scrollPositionCheckInterval);
        } else {
          prevPosition = window.scrollY;
        }
      }, 25);
    };

    // We don't want the spy to keep changing the links' states while we're smooth scrolling
    // to the target element, hence why we add a check to halt the handling until 'checkIfScrollToIsFinished'
    // completes. `window.scrollTo' doesn't provide a callback, unfortunately.
    this.isScrolling = true;
    this.updateLinksStates(targetElement);

    window.scrollTo({ top: scrollPosition, behavior: smooth ? 'smooth' : 'auto' });

    // Unlock the spy scroll handler
    checkIfScrollToIsFinished();
  }
}

ScrollSpy.defaultProps = {
  activeClassname: 'active',
  className: '',
  offset: 0
};

ScrollSpy.propTypes = {
  activeClassname: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  offset: PropTypes.number
};

export default ScrollSpy;
